import type { MRT_RowData, MRT_TableInstance } from "material-react-table";
import { useEffect, useRef } from "react";

/**
 * The page that holds a row, counted in the order the table draws its rows once sorted and filtered.
 * Undefined when the table does not draw the row at all: the search box or a filter hides it.
 */
export function pageHoldingRow(drawnRowIds: readonly string[], rowId: string, pageSize: number): number | undefined {
  const index = drawnRowIds.indexOf(rowId);
  if (index < 0 || pageSize <= 0) return undefined;
  return Math.floor(index / pageSize);
}

/**
 * Turns a paged table to the page that holds `rowId` each time `rowId` names a new row (#6617).
 *
 * A traverser lands on a row by lighting it and scrolling to it, and a row on another page is not drawn,
 * so there was nothing to light or scroll to and the landing seemed to do nothing. The table turns
 * once per new row and not on every render, so a reader who pages away from it afterwards stays where
 * they paged. A row the table does not draw, hidden by its search or a filter, turns nothing.
 */
export function useTurnToRevealedRow<TData extends MRT_RowData>(
  table: MRT_TableInstance<TData>,
  rowId: string | undefined,
): void {
  const revealedRowId = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (rowId === revealedRowId.current) return;
    revealedRowId.current = rowId;
    if (rowId === undefined) return;

    const drawnRowIds = table.getPrePaginationRowModel().rows.map((row) => row.id);
    const { pageIndex, pageSize } = table.getState().pagination;
    const page = pageHoldingRow(drawnRowIds, rowId, pageSize);
    if (page === undefined || page === pageIndex) return;
    table.setPageIndex(page);
  }, [rowId, table]);
}

/**
 * Tells `onChange` which rows the table draws, in the order it draws them once sorted and filtered, each
 * time that order changes, and `undefined` once the table is gone (#6617).
 *
 * A traverser that walks a table's rows walks them in this order. The reader can sort the table by any
 * column, and a walk in the data's own order then jumped about the rows on screen. The answer is
 * undefined after the table unmounts, so a page does not walk by the sort of a table it no longer shows.
 */
export function useReportDrawnRows<TData extends MRT_RowData>(
  table: MRT_TableInstance<TData>,
  onChange: ((rows: readonly TData[] | undefined) => void) | undefined,
): void {
  const latestOnChange = useRef(onChange);
  const reportedRowIds = useRef<string | undefined>(undefined);
  useEffect(() => {
    latestOnChange.current = onChange;
  });
  useEffect(() => {
    if (!latestOnChange.current) return;
    const drawn = table.getPrePaginationRowModel().rows;
    const rowIds = drawn.map((row) => row.id).join(",");
    if (rowIds === reportedRowIds.current) return;
    reportedRowIds.current = rowIds;
    latestOnChange.current(drawn.map((row) => row.original));
  });
  useEffect(
    () => () => {
      latestOnChange.current?.(undefined);
    },
    [],
  );
}
